import styles from "./task-card.module.scss";
import primaryArrow from "assets/images/v2/Primary_Arrow.svg";
import secondaryArrow from "assets/images/v2/Secondary_Arrow.svg";
import { getCurrentUserId, getNhDealApplicationId, setProfileActiveTab, setSelectedTaskId } from "features/authSlice";
import { NeedStatus, PendingItem, useGetNeedsQuery } from "pages/dashboard/home/dashboardService";
import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Spinner } from "../spinner/spinner";
import { AdditionalDocumentationCondition } from "utilities/Constant";
import useDesktopChecker from "hooks/useDesktopChecker";
import { isNonEmptyString } from "utilities/string";
import Pill from "../pill/pill";

type TaskCardProps = {
  readonly onNeedsLoaded?: (hasNeeds: boolean) => void;
};

export default function TaskCard({ onNeedsLoaded }: TaskCardProps) {
  const [needsList, setNeedsList] = useState<PendingItem[]>([]);
  const userId = useSelector(getCurrentUserId);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const dispatch = useDispatch();
  const isDesktop = useDesktopChecker();
  const {
    data: pendingItemsV2,
    isLoading: isNeedsListLoading,
    error,
  } = useGetNeedsQuery(
    { nhDealApplicationId, userId },
    {
      refetchOnMountOrArgChange: false,
      selectFromResult: ({ data, isLoading, error }) => ({
        data,
        isLoading,
        error,
      }),
    }
  );
  useEffect(() => {
    const filteredList = (pendingItemsV2 ?? []).filter(
      (item) =>
        item.needStatus?.toLowerCase() != NeedStatus.Satisfied.toLowerCase() &&
        item.needStatus?.toLowerCase() != NeedStatus.ReviewNeeded.toLowerCase() &&
        item.title !== AdditionalDocumentationCondition
    );
    setNeedsList(filteredList);

    onNeedsLoaded?.(filteredList.length > 0);
    
    if (filteredList.length > 0) {
      setSelectedTaskId(filteredList[0].id);
    }
  }, [pendingItemsV2]);

  const handleViewAllClick = () => {
    dispatch(setProfileActiveTab(NavigationIds.Tasks));
  };

  const handleTaskClick = (task: PendingItem) => {
    dispatch(setSelectedTaskId(task.id));
    dispatch(setProfileActiveTab(NavigationIds.Tasks));
  };

  return (
    <div className={styles.taskCard}>
      <div className={styles.heading}>
        {isDesktop && <h5 className="heading-medium">My Tasks</h5>}
        {!isDesktop && <h6 className="heading-medium">My Tasks</h6>}
      </div>
      <div className={styles.content2}>
        {error && <div className={styles.errorMessage}>We are having issues loading your tasks. Try again later.</div>}
        {isNeedsListLoading && (
          <div className={styles.spinnerContainer}>
            <Spinner isApi />
          </div>
        )}
        <div className={styles.taskContent1}>
          {needsList.slice(0, 3).map((task) => (
            <button
              className={styles.taskContentParent}
              key={task.title}
              onClick={() => handleTaskClick(task)}
              aria-label={`Select task: ${task.title}`}
            >
              <div className={styles.frameChild} />
              <div className={styles.taskContent2}>
                <div className={styles.rectangleParent}>
                  <div className={styles.taskDetails}>
                    <div className={styles.taskTile}>{task.title}</div>
                    {isNonEmptyString(task.assignedTo) && <Pill text={task.assignedTo || ""} variant="assigned" />}
                    {(task.status === "needsAttention" || !!task.note?.trim()) && <Pill text="New Comments" variant="comments" />}
                  </div>
                </div>
              </div>
              <img className={styles.primaryIcon} alt="Chevron right icon" src={secondaryArrow} />
            </button>
          ))}

          {needsList.length === 0 && !isNeedsListLoading && !error && (
            <div className={styles.noTasksMessageContainer}>
              <div className={styles.noTasksMessage}>You’re all caught up!</div>
            </div>
          )}

          {needsList.length >= 3 && (
            <div className={styles.taskContentInner}>
              <button className={styles.viewAllParent} onClick={handleViewAllClick} aria-label="View all tasks">
                <div className={styles.viewDetails}>View all</div>
                <img className={styles.chevronRightIcon} alt="Chevron right icon" src={primaryArrow} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
